import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import axios from "axios";
import OAuth from "oauth-1.0a";
import CryptoJS from "crypto-js";

const CONSUMER_KEY = process.env.REACT_APP_CONSUMER_KEY;
const CONSUMER_SECRET = process.env.REACT_APP_CONSUMER_SECRET;

// OAuth Setup
const oauth = new OAuth({
  consumer: {
    key: CONSUMER_KEY,
    secret: CONSUMER_SECRET,
  },
  signature_method: "HMAC-SHA1",
  hash_function(base_string, key) {
    return CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64);
  },
});

// Axios instance (OAuth enabled)
const oauthApi = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Apply OAuth only for requests using oauthApi
oauthApi.interceptors.request.use((config) => {
  const requestData = {
    url: config.url,
    method: config.method.toUpperCase(),
  };

  const authHeader = oauth.toHeader(oauth.authorize(requestData));

  config.headers = {
    ...config.headers,
    ...authHeader,
  };

  return config;
});

const DonationFormModal = ({
  onClose,
  campaign,
  selectedCurrency,
  selectedPaymentMethod,
  formatCurrency,
  showDonationConfetti,
  setShowDonationConfetti,

  loggedInUser,
}) => {
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [isAnonymous, setIsAnonymous] = useState(false);

  const tipOptions = [0, 10, 18, 30, 40, 50];
  const [tipPercentage, setTipPercentage] = useState(18);

  // Prefill fields when loggedInUser changes
  useEffect(() => {
    console.log("LoggedInUser Received:", loggedInUser);

    if (loggedInUser) {
      setName(loggedInUser?.name || "");
      setEmail(loggedInUser?.email || "");
      setMobile(loggedInUser?.mobile || "");
    }
  }, [loggedInUser]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  const formCampaign = campaign || {
    title:
      "My Father Is Fighting For His Life And We Need Your Support To Save Him",
    campaigner: campaign?.creator_name || "Jyotsna Venkatamathi P",
    beneficiary: campaign?.patient_name || "Mr Padmanabhan V",
    supporters: campaign?.supporters || 85,
    goal: campaign?.goal_amount || 500000,
    daysLeft: campaign?.days_left || 26,
  };

  const presetAmounts = [300, 1000, 3000];
  const tipAmount = Math.round(amount * (tipPercentage / 100));
  const totalAmount = amount + tipAmount;

  const handleAmountSelect = (amt) => {
    setAmount(amt);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(value);
    if (value) {
      setAmount(parseInt(value) || 0);
    }
  };

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);

      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // API 1: Create Donation
    const DONATION_API_URL = "https://api.donatepe.org/donations/create";

    // API 2: Create Razorpay Order (using donationId)
    const CREATE_ORDER_API_URL = "https://api.donatepe.org/donations/create-order";

    const requestBody = {
      fundraiserId: campaign?.id,
      donorName: name,
      donorEmail: email,
      donorMobile: mobile,
      amount: amount,
      tipAmount: tipAmount,
      isAnonymous: isAnonymous,
    };

    console.log("📤 Donation Request Body:", requestBody);

    try {
      // ----------------------------
      // 1️⃣ API CALL: Create Donation
      // ----------------------------
      const donationResponse = await oauthApi.post(DONATION_API_URL, requestBody);

      console.log("✅ Donation API Response:", donationResponse.data);

      const donationId = donationResponse.data?.donationId;

      if (!donationId) {
        alert("Donation created but donationId not received.");
        return;
      }

      console.log("🆔 Donation ID:", donationId);

      // ----------------------------
      // 2️⃣ API CALL: Create Razorpay Order
      // ----------------------------
      const orderRequestBody = {
        donationId: donationId,
      };

      console.log("📤 Order Request Body:", orderRequestBody);

      const orderResponse = await oauthApi.post(
        CREATE_ORDER_API_URL,
        orderRequestBody
      );

      console.log("✅ Order API Response:", orderResponse.data);

      if (!orderResponse.data?.success) {
        alert("Failed to create Razorpay order.");
        return;
      }

      const { orderId, amount, currency } = orderResponse.data;

      // ----------------------------
      // 3️⃣ LOAD RAZORPAY SCRIPT
      // ----------------------------
      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        alert("Razorpay SDK failed to load. Check your internet connection.");
        return;
      }

      // ----------------------------
      // 4️⃣ LAUNCH RAZORPAY CHECKOUT
      // ----------------------------
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Razorpay Key ID
        amount: amount, // already in paise from backend response
        currency: currency,
        name: "Donation Platform",
        description: `Donation for ${formCampaign.title}`,
        order_id: orderId,

       handler: async function (response) {
  console.log("Razorpay Payment Success Full Response:", response);

  setShowDonationConfetti(true);

  try {
    // 3️⃣ API CALL: Confirm Payment (third API)
    const CONFIRM_PAYMENT_API_URL = "https://api.donatepe.org/donations/verify";

    const confirmPaymentBody = {
      donationId: donationId, // from previous API
      razorpay_order_id: response.razorpay_order_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_signature: response.razorpay_signature,
    };

    console.log("Confirm Payment Request Body:", confirmPaymentBody);

    const confirmResponse = await oauthApi.post(
      CONFIRM_PAYMENT_API_URL,
      confirmPaymentBody
    );

    console.log("Confirm Payment API Response:", confirmResponse.data);

    if (confirmResponse.data?.success !== true && confirmResponse.data?.success !== "true") {
      alert("Payment succeeded but confirmation failed on server.");
      setShowDonationConfetti(false);
      return;
    }

    // Show Thank You Box
    const thankYouBox = document.createElement("div");
    thankYouBox.innerHTML = `
      <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white p-8 rounded-3xl shadow-2xl text-center max-w-sm w-full">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p class="text-gray-700 mb-6">Your contribution has been successfully received.</p>
          <a href="/" class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow hover:bg-blue-700 transition">Go to Home</a>
        </div>
      </div>
    `;
    document.body.appendChild(thankYouBox);

    setShowDonationConfetti(false);

    // Optional: auto-redirect after 5 seconds
    setTimeout(() => {
      window.location.href = "/";
    }, 5000);

  } catch (err) {
    console.error("Confirm Payment API Error:", err);
    alert(
      "Payment succeeded, but confirming payment on server failed. Please contact support."
    );
    setShowDonationConfetti(false);
  }
},




        prefill: {
          name: name,
          email: email,
          contact: mobile,
        },

        theme: {
          color: "#00B7B3",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        console.error("❌ Razorpay Payment Failed:", response.error);

        alert(
          `Payment Failed!\nReason: ${response.error.description || "Unknown"}`
        );
      });

      razorpay.open();
    } catch (error) {
      console.error("❌ Donation/Order API Error:", error);

      alert(
        error?.response?.data?.message ||
          "Something went wrong while processing donation."
      );
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white flex justify-between items-center px-6 py-4 z-10 border-b rounded-t-3xl">
          <h2 className="text-lg font-bold text-gray-900">
            Contribute to Fundraiser
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="px-8 pb-8 pt-6">
          {/* Title */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Choose a contribution amount
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Most contributors donate around{" "}
              <span className="font-semibold text-blue-700">₹1000</span>
            </p>
          </div>

          {/* Amount Buttons */}
          <div className="mb-8">
            <div className="flex justify-center gap-4 mb-6">
              {presetAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => handleAmountSelect(amt)}
                  className={`px-8 py-4 rounded-2xl border-2 text-lg font-bold transition-all duration-200 ${
                    amount === amt && !customAmount
                      ? "border-blue-600 bg-blue-50 text-blue-700 shadow-md scale-105"
                      : "border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50"
                  }`}
                >
                  ₹{amt.toLocaleString()}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="text-center">
              <input
                type="text"
                placeholder="Enter Custom Amount"
                value={customAmount}
                onChange={handleCustomAmount}
                className="w-72 px-6 py-4 border-2 border-gray-300 rounded-2xl text-center text-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 shadow-sm"
              />
            </div>
          </div>

          {/* Tip Section */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-3xl mb-6 border border-gray-200 shadow-md">
            <p className="text-sm text-gray-700 mb-5 text-center font-medium">
              Platform fee is{" "}
              <span className="font-bold text-green-700">0%</span> for this
              fundraiser. Support us with a small tip 💙
            </p>

            {/* Dropdown Tip Selector */}
            <div className="mb-5">
              <label className="text-sm font-bold text-gray-800 block mb-2 text-center">
                Select Tip Percentage
              </label>

              <div className="flex justify-center">
                <select
                  value={tipPercentage}
                  onChange={(e) => setTipPercentage(Number(e.target.value))}
                  className="px-5 py-3 rounded-xl border border-gray-300 shadow-sm text-gray-800 font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {tipOptions.map((tip) => (
                    <option key={tip} value={tip}>
                      {tip}%
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-gray-500 mt-3 text-center">
                Tip helps us maintain the platform and reach more donors.
              </p>
            </div>

            {/* Tip + Total */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm text-center">
                <p className="text-xs text-gray-500 font-semibold">Tip Amount</p>
                <p className="text-lg font-bold text-gray-900 mt-1">
                  INR {tipAmount.toLocaleString()}
                </p>
              </div>

              <div className="bg-blue-600 p-4 rounded-2xl shadow-lg text-center">
                <p className="text-xs text-blue-100 font-semibold">
                  Total Payable
                </p>
                <p className="text-lg font-bold text-white mt-1">
                  INR {totalAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                placeholder="Enter your name"
              />
            </div>

            {/* Anonymous */}
            <div className="flex items-center bg-gray-50 p-4 rounded-2xl border border-gray-200 shadow-sm">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded"
              />
              <label
                htmlFor="anonymous"
                className="ml-3 text-sm font-medium text-gray-700"
              >
                Make my contribution anonymous
              </label>
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Email ID *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                placeholder="Enter your email"
              />
            </div>

            {/* Mobile */}
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">
                Your Mobile Number *
              </label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                placeholder="Enter your mobile number"
              />
              <p className="text-xs text-gray-500 mt-2 ml-1">
                Payment updates will be sent to this number.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-[#00B7B3] to-[#009c99] hover:from-[#009c99] hover:to-[#007f7c] text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 mt-8"
            >
              Proceed To Contribute ₹{totalAmount.toLocaleString()}
            </button>
          </form>

          {/* Footer */}
          <p className="text-xs text-gray-400 mt-6 text-center">
            By continuing, you agree to our terms and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationFormModal;
